import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { RQAPICall, RQAPICallWithData } from 'store/APIClient';

import { CommentT } from 'routes/Dashboard/components/ChartActions/CommentSection/Comment';
import { EventTypes, FullInscriptionEventT, InscriptionEventT } from '../types';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { INSCRIPTION_ID } from 'routes/BudgetModule/config/constants';

const useInscriptionEventQuery = (
    inscriptionId: number | undefined,
    enabled: boolean,
): UseQueryResult<InscriptionEventT[]> =>
    useQuery({
        queryKey: ['inscription_events', inscriptionId],

        queryFn: async () => {
            const response = await RQAPICall({
                endpoint: `budget/inscription/${inscriptionId}/events`,
                method: 'GET',
            });
            return response.content.data;
        },

        enabled,
    });

type InscriptionCommentT = CommentT & { user: { email: string } };

const useInscriptionCommentsQuery = (
    inscriptionId: number | undefined,
    enabled: boolean,
): UseQueryResult<InscriptionCommentT[]> =>
    useQuery({
        queryKey: ['inscription_comments', inscriptionId],

        queryFn: async () => {
            const response = await RQAPICallWithData<CommentT[]>({
                endpoint: `budget/inscription/${inscriptionId}/comments`,
                method: 'GET',
            });
            return response.content.data;
        },

        enabled,
    });

/**
 * Hook to get all events related to an inscription.
 * It combines both inscription events and comments into a single array.
 * Each event is enriched with the budget proposal information.
 * The events are sorted by date in descending order, and the last event is marked as the last item.
 */
export const useGetAllInscriptionsEvent = (
    inscriptionData: InscriptionT | undefined,
    budgetProposal: BudgetProposalT,
): { events: FullInscriptionEventT[]; isLoading: boolean } => {
    const { data: events = [], isLoading: isInscriptionEventsLoading } =
        useInscriptionEventQuery(
            inscriptionData?.[INSCRIPTION_ID],
            inscriptionData !== undefined,
        );

    const { data: comments = [], isLoading: isInscriptionCommentsLoading } =
        useInscriptionCommentsQuery(
            inscriptionData?.[INSCRIPTION_ID],
            inscriptionData !== undefined,
        );

    const totalEvents: FullInscriptionEventT[] = [
        ...events.map((event) => ({
            ...event,
            budgetProposal,
        })),
        ...comments.map((c) => ({
            ...c,
            type: EventTypes.COMMENT,
            budgetProposal,
        })),
    ];
    if (totalEvents && totalEvents.length > 0) {
        totalEvents.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        totalEvents[totalEvents.length - 1].lastItem = true;
    }
    return {
        events: totalEvents,
        isLoading: isInscriptionEventsLoading || isInscriptionCommentsLoading,
    };
};
